/**
 * One contributor, in full.
 *
 * Laid out in the order the questions actually get asked: who is this, how
 * did they get here, what have they been doing lately, what have they made,
 * and what have we paid them. The audition they were accepted on sits in the
 * same page as the work that followed it, which is the only way to tell
 * whether the sample was representative.
 *
 * The one thing this screen can change is whether the account is active,
 * because that is the decision somebody reaches *from* reading all of this.
 * Everything else — reviewing a take, paying a balance — belongs to the
 * screen built for it.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { BackOfficeNav } from "../components/BackOfficeNav";
import { ClipPlayer } from "../components/ClipPlayer";
import { Meter, MeterRow } from "../components/Meter";
import { Pagination } from "../components/Pagination";
import { RefreshButton } from "../components/RefreshButton";
import { ReviewDecision } from "../components/ReviewDecision";
import { ReviewSignIn } from "../components/ReviewSignIn";
import { StatusPill, PublishPill } from "../components/StatusPill";
import { useToast } from "../hooks/useToast";
import { isCredentialFailure } from "../lib/backOffice";
import { clearReviewCredentials, readReviewCredentials } from "../lib/session";
import { approvalLabel } from "../lib/roster";
import { formatDate, formatDuration } from "../lib/validation";
import { reviews as reviewsApi, rosterAdmin, type ReviewCreds } from "../api/endpoints";
import type {
  Contribution,
  ContributionList,
  ContributorProfile,
  DayTally,
  RecordingStatus,
  ReviewDecision as Decision,
} from "../types/api";

const PAGE_SIZE = 25;

const WORK_FILTERS: Array<{ id: RecordingStatus | "all"; label: string }> = [
  { id: "all", label: "Everything" },
  { id: "approved", label: "Approved" },
  { id: "pending_approval", label: "In review" },
  { id: "rerecord", label: "Re-record" },
  { id: "rejected", label: "Not accepted" },
];

/**
 * The first page of work comes with the profile, so the default view costs
 * one request. Only a filter or a second page needs the list endpoint.
 */
function workFromProfile(profile: ContributorProfile): ContributionList {
  return {
    contributions: profile.recent_contributions,
    total: profile.metrics.recordings_total,
    page: 1,
    page_size: PAGE_SIZE,
    has_more: profile.metrics.recordings_total > profile.recent_contributions.length,
  };
}

export function AdminContributor() {
  const { contributorId = "" } = useParams();
  const toast = useToast();
  const [creds, setCreds] = useState<ReviewCreds | null>(() => readReviewCredentials());
  const [profile, setProfile] = useState<ContributorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const [workStatus, setWorkStatus] = useState<RecordingStatus | "all">("all");
  const [workPage, setWorkPage] = useState(1);
  const [workLoading, setWorkLoading] = useState(false);
  const [deciding, setDeciding] = useState<string | null>(null);
  // Reading a contributor is `contributor:read`; judging their work is
  // `recording:review`. Finance holds the first and not the second, so
  // the buttons are hidden rather than shown and then refused.
  const [canReview, setCanReview] = useState(false);
  // Keyed by the request that produced it, so the previous filter's results
  // are never shown under the new filter's heading while the fetch is in
  // flight.
  const [fetchedWork, setFetchedWork] = useState<{
    key: string;
    list: ContributionList;
  } | null>(null);

  const workKey = `${workStatus}:${workPage}`;
  // The default view is the page the profile already carried, so opening
  // somebody costs one request. Anything else comes from the list endpoint.
  const defaultView = workStatus === "all" && workPage === 1;
  const work: ContributionList | null = defaultView
    ? profile
      ? workFromProfile(profile)
      : null
    : fetchedWork?.key === workKey
      ? fetchedWork.list
      : null;

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    void (async () => {
      try {
        const me = await reviewsApi.whoami(creds);
        if (!cancelled) setCanReview(me.permissions.includes("recording:review"));
      } catch {
        // whoami itself needs `recording:read`, so a refusal here is the
        // answer rather than an error worth showing anybody.
        if (!cancelled) setCanReview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creds]);

  useEffect(() => {
    if (!creds || !contributorId) return;
    let cancelled = false;
    void (async () => {
      try {
        setLoadError(null);
        const result = await rosterAdmin.profile(creds, contributorId, PAGE_SIZE);
        if (!cancelled) setProfile(result);
      } catch (err) {
        if (cancelled) return;
        if (isCredentialFailure(err)) {
          clearReviewCredentials();
          setCreds(null);
        } else {
          setLoadError((err as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creds, contributorId, nonce]);

  useEffect(() => {
    if (!creds || defaultView) return;
    let cancelled = false;
    void (async () => {
      setWorkLoading(true);
      try {
        const list = await rosterAdmin.contributions(creds, contributorId, {
          status: workStatus === "all" ? undefined : workStatus,
          page: workPage,
        });
        if (!cancelled) setFetchedWork({ key: workKey, list });
      } catch (err) {
        if (!cancelled) toast.show((err as Error).message, "error");
      } finally {
        if (!cancelled) setWorkLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creds, contributorId, defaultView, workStatus, workPage, workKey, toast]);

  async function decide(
    recordingId: string,
    decision: Decision,
    reason?: string,
    note?: string,
  ) {
    if (!creds) return;
    setDeciding(recordingId);
    try {
      await reviewsApi.decide(creds, recordingId, { decision, reason, note });
      toast.show(
        decision === "approved"
          ? "Approved — the contributor has been paid."
          : decision === "rerecord"
            ? "Sent back for another take."
            : "Rejected.",
        "success",
      );
      // The whole page: a verdict moves their counts, their balance and
      // their ledger, and showing a stale total next to a changed verdict
      // is how somebody ends up approving the same take twice.
      reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setDeciding(null);
    }
  }

  async function setStatus(status: "active" | "suspended") {
    if (!creds) return;
    setBusy(true);
    try {
      await rosterAdmin.setStatus(creds, contributorId, status);
      toast.show(
        status === "suspended"
          ? "Suspended. They can't claim or record anything now."
          : "Reactivated.",
        "success",
      );
      setConfirming(false);
      reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setBusy(false);
    }
  }

  if (!creds) return <ReviewSignIn onSubmit={setCreds} />;

  if (loading && !profile) {
    return (
      <Shell>
        <div className="stack" aria-busy="true" aria-label="Loading contributor">
          {[0, 1, 2].map((row) => (
            <div key={row} className="panel panel-tight log-skeleton">
              <div className="skeleton-line skeleton-title" />
              <div className="skeleton-line skeleton-meta" />
            </div>
          ))}
        </div>
      </Shell>
    );
  }

  if (loadError || !profile) {
    return (
      <Shell>
        <div className="panel empty">
          <h3>Couldn't load this contributor</h3>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            {loadError ?? "Nothing came back for that id."}
          </p>
          <button type="button" className="btn btn-ghost mt-16" onClick={reload}>
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  const { contributor, metrics, origin } = profile;
  const suspended = contributor.status === "suspended";

  return (
    <Shell onRefresh={reload} refreshing={loading}>
      <div className="row-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="display" style={{ fontSize: "clamp(22px,4vw,30px)", margin: 0 }}>
            {contributor.name}
          </h1>
          <div className="log-meta muted" style={{ marginTop: 6 }}>
            {contributor.platform_account?.user_handle && (
              <span className="mono">@{contributor.platform_account.user_handle} · </span>
            )}
            {contributor.email}
            {profile.country_name ? ` · ${profile.country_name}` : ""}
          </div>
        </div>
        <div className="log-pills">
          {suspended ? (
            <span className="pill pill-rejected">Suspended</span>
          ) : (
            <span className="pill pill-approved">Active</span>
          )}
          {!contributor.onboarding.completed && (
            <span className="pill pill-pending">Onboarding</span>
          )}
        </div>
      </div>

      <MeterRow className="mt-24">
        <Meter label="Approved" value={metrics.approved.toLocaleString()} />
        <Meter label="In review" value={metrics.pending.toLocaleString()} />
        <Meter label="Approval rate" value={approvalLabel(metrics.approval_rate)} />
      </MeterRow>
      <MeterRow>
        <Meter label="On air" value={metrics.published.toLocaleString()} />
        <Meter label="Recorded this week" value={metrics.recorded_last_7_days.toLocaleString()} />
        <Meter
          label="Average length"
          value={
            metrics.average_duration_seconds
              ? formatDuration(metrics.average_duration_seconds)
              : "—"
          }
        />
      </MeterRow>
      <MeterRow>
        <Meter label="Earned" value={`$${metrics.lifetime_earned.formatted}`} />
        <Meter
          label="Owed"
          value={`$${metrics.balance.formatted}`}
          tone={metrics.balance.cents > 0 ? "warn" : undefined}
        />
        <Meter label="Paid out" value={`$${metrics.paid_out.formatted}`} />
      </MeterRow>

      <Activity days={profile.by_day} />

      <Details profile={profile} />

      {origin && <Origin origin={origin} />}

      <div className="section-heading mt-24">
        <h2>
          Their work{" "}
          <span className="muted" style={{ fontWeight: 400, fontSize: 14 }}>
            {work ? `· ${work.total.toLocaleString()}` : ""}
          </span>
        </h2>
      </div>

      <div className="filter-row">
        {WORK_FILTERS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`chip ${workStatus === entry.id ? "chip-active" : ""}`}
            onClick={() => {
              setWorkStatus(entry.id);
              setWorkPage(1);
            }}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {!work ? (
        <div className="stack mt-16" aria-busy="true" aria-label="Loading their work">
          {[0, 1].map((row) => (
            <div key={row} className="panel panel-tight log-skeleton">
              <div className="skeleton-line skeleton-title" />
              <div className="skeleton-line skeleton-meta" />
            </div>
          ))}
        </div>
      ) : work.contributions.length === 0 ? (
        <div className="panel empty mt-16">
          <h3>Nothing here</h3>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            {workStatus === "all"
              ? "They haven't recorded anything yet."
              : "Nothing of theirs is in that state."}
          </p>
        </div>
      ) : (
        <div className="stack mt-16" aria-busy={workLoading || undefined}>
          {work.contributions.map((row) => (
            <ContributionCard
              key={row.recording_id}
              row={row}
              canReview={canReview}
              busy={deciding === row.recording_id}
              onDecide={decide}
            />
          ))}
        </div>
      )}

      {work && (
        <Pagination
          page={work.page}
          hasMore={work.has_more}
          total={work.total}
          noun={work.total === 1 ? "recording" : "recordings"}
          busy={workLoading}
          onChange={setWorkPage}
          // Newest first, so forward is further back through their work.
          labels={{ back: "← Newer", forward: "Older →" }}
        />
      )}

      <Ledger profile={profile} />
      <Payouts profile={profile} />

      <div className="panel mt-24">
        <div className="card-label">Account</div>
        <p className="muted" style={{ margin: "0 0 12px", fontSize: 13 }}>
          {suspended
            ? "Suspended: they can sign in, but nothing is handed to them and they cannot record."
            : "Suspending stops work being handed out. It changes nothing about what they have already earned."}
        </p>
        {confirming ? (
          <div className="btn-row">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={() => void setStatus(suspended ? "active" : "suspended")}
            >
              {busy ? "Saving…" : suspended ? "Yes, reactivate" : "Yes, suspend"}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setConfirming(true)}
          >
            {suspended ? "Reactivate this account" : "Suspend this account"}
          </button>
        )}
      </div>
    </Shell>
  );
}

function Shell({
  children,
  onRefresh,
  refreshing,
}: {
  children: React.ReactNode;
  onRefresh?: () => void;
  refreshing?: boolean;
}) {
  return (
    <Layout showNav={false} showAccount={false} title="Contributor">
      <section className="section">
        <div className="wrap">
          <BackOfficeNav />
          <div className="row-between">
            <Link to="/admin/contributors" className="btn btn-ghost btn-sm">
              ← All contributors
            </Link>
            {onRefresh && <RefreshButton onClick={onRefresh} busy={Boolean(refreshing)} />}
          </div>
          <div className="mt-16">{children}</div>
        </div>
      </section>
    </Layout>
  );
}

/**
 * A month of recording, one bar a day.
 *
 * Empty days are drawn as empty bars rather than left out: three bars side by
 * side would otherwise read as three days on the trot whether they were
 * consecutive or a month apart.
 */
function Activity({ days }: { days: DayTally[] }) {
  const peak = useMemo(
    () => Math.max(1, ...days.map((entry) => entry.recordings)),
    [days],
  );
  const total = days.reduce((sum, entry) => sum + entry.recordings, 0);

  return (
    <div className="panel mt-24">
      <div className="row-between">
        <div className="card-label" style={{ margin: 0 }}>
          Last 30 days
        </div>
        <span className="muted" style={{ fontSize: 12 }}>
          {total.toLocaleString()} {total === 1 ? "recording" : "recordings"}
        </span>
      </div>
      {total === 0 ? (
        <p className="muted" style={{ margin: "12px 0 0", fontSize: 13 }}>
          Nothing recorded this month.
        </p>
      ) : (
        <div className="activity" role="img" aria-label={`${total} recordings in the last 30 days`}>
          {days.map((entry) => (
            <div
              key={entry.day}
              className={`activity-bar${entry.recordings ? " is-on" : ""}`}
              style={{ height: `${Math.max(3, (entry.recordings / peak) * 100)}%` }}
              title={`${entry.day}: ${entry.recordings}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="detail-row">
      <span className="k">{label}</span>
      <span className="v">{value}</span>
    </div>
  );
}

function Details({ profile }: { profile: ContributorProfile }) {
  const { contributor, metrics } = profile;
  return (
    <div className="panel mt-24">
      <div className="card-label">Details</div>
      <div className="detail-grid">
        <Row label="Contributor id" value={<span className="mono">{contributor.contributor_id}</span>} />
        <Row label="Email" value={contributor.email} />
        <Row label="WhatsApp" value={contributor.phone} />
        <Row
          label="Paid to"
          value={
            profile.payout_details
              ? `${profile.payout_details.bank_name} ····${profile.payout_details.account_number_last4} · ${profile.payout_details.currency}`
              : contributor.paypal_email
                ? `PayPal · ${contributor.paypal_email}`
                : "No payout details yet"
          }
        />
        <Row label="Account name" value={profile.payout_details?.full_legal_name} />
        <Row label="Bank country" value={profile.payout_details?.country_name} />
        <Row label="Voice" value={contributor.gender} />
        <Row label="Country" value={profile.country_name ?? contributor.country} />
        <Row label="Rate" value={`$${(contributor.rate_cents / 100).toFixed(2)} a response`} />
        <Row label="Languages" value={contributor.languages?.join(", ")} />
        <Row label="Date of birth" value={contributor.date_of_birth} />
        <Row label="Joined" value={formatDate(contributor.created_at)} />
        <Row label="Last signed in" value={formatDate(contributor.last_login_at)} />
        <Row
          label="Terms agreed"
          value={
            contributor.onboarding.terms_accepted
              ? `v${contributor.onboarding.terms_version ?? "?"} · ${formatDate(
                  contributor.onboarding.terms_accepted_at,
                )}`
              : "Not yet"
          }
        />
        <Row
          label="First recorded"
          value={metrics.first_recorded_at ? formatDate(metrics.first_recorded_at) : "Never"}
        />
        <Row
          label="Last recorded"
          value={metrics.last_recorded_at ? formatDate(metrics.last_recorded_at) : "Never"}
        />
        <Row label="Holding now" value={`${profile.open_assignments} to record`} />
        <Row label="Passed on" value={`${profile.skipped} skipped`} />
      </div>
      {contributor.bio && (
        <p className="muted" style={{ margin: "14px 0 0", fontSize: 13 }}>
          {contributor.bio}
        </p>
      )}
    </div>
  );
}

function Origin({ origin }: { origin: NonNullable<ContributorProfile["origin"]> }) {
  const applied = Boolean(origin.application_id);
  return (
    <div className="panel mt-24">
      <div className="card-label">How they got in</div>
      {applied ? (
        <>
          <div className="detail-grid">
            <Row label="Applied" value={formatDate(origin.applied_at)} />
            <Row label="Accepted" value={formatDate(origin.decided_at)} />
            <Row label="Accepted by" value={origin.decided_by_name} />
            <Row label="Came from" value={origin.source ?? "A direct link"} />
            <Row
              label="Invite code"
              value={origin.invite_code && <span className="mono">{origin.invite_code}</span>}
            />
            <Row label="Applied from" value={origin.applied_country} />
          </div>
          {origin.sample_url ? (
            <div className="mt-16">
              <div className="card-label" style={{ marginBottom: 8 }}>
                The sample they were accepted on
                {origin.prompt_title ? ` · ${origin.prompt_title}` : ""}
              </div>
              <ClipPlayer
                url={origin.sample_url}
                durationSeconds={origin.sample_duration_seconds}
              />
            </div>
          ) : (
            <p className="muted" style={{ margin: "12px 0 0", fontSize: 13 }}>
              The sample isn't available.
            </p>
          )}
        </>
      ) : (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Invited directly with code <span className="mono">{origin.invite_code}</span> — no
          application went through the public page.
        </p>
      )}
    </div>
  );
}

function ContributionCard({
  row,
  canReview,
  busy,
  onDecide,
}: {
  row: Contribution;
  canReview: boolean;
  busy: boolean;
  onDecide: (
    recordingId: string,
    decision: Decision,
    reason?: string,
    note?: string,
  ) => Promise<void>;
}) {
  // A superseded take is the earlier attempt at something already
  // re-recorded; the API refuses a verdict on one, so there is nothing to
  // offer. Everything else can be judged, including changing a verdict that
  // was already given.
  const decidable = canReview && row.status !== "superseded";
  const alreadyRuled = row.status !== "pending_approval";

  return (
    <article className="panel panel-tight">
      <div className="row-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div className="log-title">{row.title}</div>
          <div className="log-meta muted">
            <span className="mono">{row.reference_tag}</span>
            {row.category ? ` · ${row.category}` : ""}
            {row.created_at ? ` · ${formatDate(row.created_at)}` : ""}
            {row.duration_seconds ? ` · ${formatDuration(row.duration_seconds)}` : ""}
          </div>
        </div>
        <div className="log-pills">
          <StatusPill status={row.status} />
          <PublishPill status={row.publish_status} />
        </div>
      </div>

      {row.text_response && <p className="log-text">{row.text_response}</p>}

      {row.playback_url && (
        <div className="mt-8">
          <ClipPlayer url={row.playback_url} durationSeconds={row.duration_seconds} />
        </div>
      )}

      {/* The same shape My Log uses to show a contributor their verdict, so
          the back office and the person it was about read the same thing. */}
      {(row.review_note || row.review_reason) && (
        <div
          className={`review-note ${
            row.status === "approved" ? "review-note-ok" : "review-note-warn"
          }`}
        >
          {row.review_reason && (
            <div>
              <b>Reason:</b> {row.review_reason.replace(/_/g, " ")}
            </div>
          )}
          {row.review_note && (
            <div>
              <b>{row.status === "approved" ? "Reviewer" : "Note"}:</b> {row.review_note}
            </div>
          )}
          {row.reviewed_by_name && (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {row.reviewed_by_name}
              {row.reviewed_at ? ` · ${formatDate(row.reviewed_at)}` : ""}
            </div>
          )}
        </div>
      )}

      <div className="row-between mt-8">
        <span className="muted" style={{ fontSize: 12 }}>
          {row.earned ? "Credited" : "Not credited"}
        </span>
        {/* `log-pay` styles the amount itself, the way My Log does it. */}
        <span className={row.earned ? "log-pay log-pay-earned" : "log-pay muted"}>
          {row.earned ? "+" : ""}${row.compensation.formatted}
        </span>
      </div>

      {decidable && (
        <ReviewDecision
          recordingId={row.recording_id}
          busy={busy}
          onDecide={onDecide}
          // Nothing to approve on something already approved: the ledger
          // credits once per recording, so the button would do nothing and
          // say it had. Rejecting stays available — undoing a mistaken
          // approval is the reason to be looking at this at all.
          canApprove={row.status !== "approved"}
          warning={
            alreadyRuled
              ? row.earned
                ? "Already approved and paid. Rejecting this reverses the credit, unless it has already been redeemed."
                : "Already decided. Changing the verdict replaces it and tells the contributor again."
              : undefined
          }
        />
      )}
    </article>
  );
}

function Ledger({ profile }: { profile: ContributorProfile }) {
  if (profile.ledger.length === 0) return null;
  return (
    <div className="panel mt-24">
      <div className="card-label">Money, most recent first</div>
      {profile.ledger.map((entry) => (
        <div key={entry.entry_id} className="ledger-row">
          <div style={{ minWidth: 0 }}>
            <div>{entry.reason}</div>
            <div className="log-meta muted">{formatDate(entry.created_at)}</div>
          </div>
          <span
            className={`ledger-amount ${
              entry.amount.cents < 0 ? "ledger-debit" : "ledger-credit"
            }`}
          >
            {entry.amount.cents < 0 ? "−" : "+"}$
            {Math.abs(entry.amount.cents / 100).toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );
}

function Payouts({ profile }: { profile: ContributorProfile }) {
  if (profile.payouts.length === 0) return null;
  return (
    <div className="panel mt-24">
      <div className="card-label">Payouts</div>
      {profile.payouts.map((payout) => (
        <div key={payout.payout_id} className="ledger-row">
          <div style={{ minWidth: 0 }}>
            <div>
              ${payout.amount.formatted} to{" "}
              <span className="mono">
                {payout.payout_details
                  ? `${payout.payout_details.bank_name} ····${payout.payout_details.account_number_last4}`
                  : payout.paypal_email || "—"}
              </span>
            </div>
            <div className="log-meta muted">
              asked {formatDate(payout.requested_at)}
              {payout.processed_at ? ` · settled ${formatDate(payout.processed_at)}` : ""}
              {payout.provider_reference ? ` · ${payout.provider_reference}` : ""}
              {payout.failure_reason ? ` · ${payout.failure_reason}` : ""}
            </div>
          </div>
          <span
            className={`pill ${
              payout.status === "paid"
                ? "pill-approved"
                : payout.status === "failed" || payout.status === "cancelled"
                  ? "pill-rejected"
                  : "pill-pending"
            }`}
          >
            {payout.status}
          </span>
        </div>
      ))}
    </div>
  );
}
